class B2HNorthStar extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.triggeringElement = null;
    this.dialog = this.querySelector('[data-concept-dialog]');
    this.heroVideo = this.querySelector('[data-hero-video]');
    this.heroVideoToggle = this.querySelector('[data-hero-video-toggle]');
    this.classList.add('is-enhanced');

    this.addEventListener('click', this.onClick.bind(this), {
      signal: this.abortController.signal,
    });
    this.addEventListener('change', this.onChange.bind(this), {
      signal: this.abortController.signal,
    });

    if (this.dialog) {
      this.dialog.addEventListener('close', this.onDialogClose.bind(this), {
        signal: this.abortController.signal,
      });
      this.dialog.addEventListener('click', this.onDialogBackdropClick.bind(this), {
        signal: this.abortController.signal,
      });
      this.dialog.addEventListener('keydown', this.onDialogKeydown.bind(this), {
        signal: this.abortController.signal,
      });
    }

    this.initializeHeroVideo();
    this.initializeReveals();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.revealObserver?.disconnect();
    this.heroVideo?.pause();

    if (this.dialog?.open) {
      this.dialog.close();
    }

    this.triggeringElement?.focus();
    this.triggeringElement = null;
  }

  onClick(event) {
    const heroVideoToggle = event.target.closest('[data-hero-video-toggle]');
    if (heroVideoToggle && this.contains(heroVideoToggle)) {
      this.toggleHeroVideo();
      return;
    }

    const contextButton = event.target.closest('[data-context]');
    if (contextButton && this.contains(contextButton)) {
      this.selectContext(contextButton);
      return;
    }

    const routeButton = event.target.closest('[data-route]');
    if (routeButton && this.contains(routeButton)) {
      this.selectRoute(routeButton);
      return;
    }

    const conceptTrigger = event.target.closest('[data-concept]');
    if (conceptTrigger && this.contains(conceptTrigger)) {
      event.preventDefault();
      this.openConcept(conceptTrigger);
      return;
    }

    if (event.target.closest('[data-dialog-close], [data-dialog-dismiss]')) {
      this.dialog?.close();
      return;
    }

  }

  onChange(event) {
    const routeSelect = event.target.closest('[data-mot-select]');
    if (!routeSelect || !this.contains(routeSelect)) return;

    const selectedButton = [...this.querySelectorAll('[data-context][data-route]')]
      .find((button) => button.dataset.route === routeSelect.value);
    if (selectedButton) this.selectContext(selectedButton);
  }

  selectContext(selectedButton) {
    this.querySelectorAll('[data-context]').forEach((button) => {
      const selected = button === selectedButton;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });

    const routeTarget = selectedButton.dataset.routeTarget;
    const targetRoute = [...this.querySelectorAll('[data-route]')]
      .find((button) => button.dataset.route === routeTarget);
    if (targetRoute) this.selectRoute(targetRoute);
  }

  selectRoute(selectedButton) {
    this.querySelectorAll('[data-route]').forEach((button) => {
      const selected = button === selectedButton;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });

    const stage = selectedButton.closest('.b2h-ns-mot-stage');
    if (stage) stage.dataset.selectedRoute = selectedButton.dataset.route;

    const routeSelect = this.querySelector('[data-mot-select]');
    if (routeSelect && routeSelect.value !== selectedButton.dataset.route) {
      routeSelect.value = selectedButton.dataset.route;
    }

    const title = this.querySelector('[data-route-result-title]');
    const copy = this.querySelector('[data-route-result-copy]');
    const tags = this.querySelector('[data-route-result-tags]');
    const link = this.querySelector('[data-route-result-link]');
    const media = this.querySelector('[data-route-result-media]');
    const mediaCaption = this.querySelector('[data-route-result-media-caption]');

    if (title) title.textContent = selectedButton.dataset.title || '';
    if (copy) copy.textContent = selectedButton.dataset.copy || '';
    if (link && selectedButton.dataset.href) link.href = selectedButton.dataset.href;
    if (media && selectedButton.dataset.mediaSrc) {
      media.src = selectedButton.dataset.mediaSrc;
      media.alt = selectedButton.dataset.mediaAlt || '';
    }
    if (mediaCaption) mediaCaption.textContent = selectedButton.dataset.mediaCaption || '';
    if (tags) {
      tags.replaceChildren(
        ...[selectedButton.dataset.tagOne, selectedButton.dataset.tagTwo]
          .filter(Boolean)
          .map((tag) => {
            const item = document.createElement('li');
            item.textContent = tag;
            return item;
          }),
      );
    }
  }

  initializeHeroVideo() {
    if (!this.heroVideo || !this.heroVideoToggle) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.heroVideo.addEventListener('play', () => this.updateHeroVideoState(true), {
      signal: this.abortController.signal,
    });
    this.heroVideo.addEventListener('pause', () => this.updateHeroVideoState(false), {
      signal: this.abortController.signal,
    });

    if (reducedMotion) {
      this.heroVideo.pause();
      this.updateHeroVideoState(false);
      return;
    }

    const playRequest = this.heroVideo.play();
    if (playRequest && typeof playRequest.catch === 'function') {
      playRequest.catch(() => this.updateHeroVideoState(false));
    }
  }

  toggleHeroVideo() {
    if (!this.heroVideo) return;

    if (!this.heroVideo.paused) {
      this.heroVideo.pause();
      return;
    }

    const playRequest = this.heroVideo.play();
    if (playRequest && typeof playRequest.catch === 'function') {
      playRequest.catch(() => this.updateHeroVideoState(false));
    }
  }

  updateHeroVideoState(isPlaying) {
    if (!this.heroVideoToggle) return;

    const label = isPlaying
      ? this.heroVideoToggle.dataset.pauseLabel
      : this.heroVideoToggle.dataset.playLabel;
    const icon = this.heroVideoToggle.querySelector('[data-hero-video-icon]');
    this.heroVideoToggle.dataset.videoState = isPlaying ? 'playing' : 'paused';
    this.heroVideoToggle.setAttribute('aria-label', label || '');
    if (icon) icon.textContent = isPlaying ? 'Ⅱ' : '▶';
  }

  openConcept(trigger) {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    const title = this.querySelector('[data-dialog-title]');
    const description = this.querySelector('[data-dialog-description]');
    const progress = this.querySelector('[data-dialog-progress]');
    const unknown = this.querySelector('[data-dialog-unknown]');
    const interestLink = this.querySelector('[data-concept-interest]');

    if (title) title.textContent = trigger.dataset.title || '';
    if (description) description.textContent = trigger.dataset.description || '';
    if (progress) progress.textContent = trigger.dataset.progress || '';
    if (unknown) unknown.textContent = trigger.dataset.unknown || '';
    if (interestLink && trigger.dataset.href) interestLink.href = trigger.dataset.href;

    this.dialog.dataset.b2hMeasurementConcept = trigger.dataset.concept || '';

    this.triggeringElement = trigger;
    this.dialog.showModal();
    this.dialog.querySelector('[data-dialog-close]')?.focus();
  }

  onDialogClose() {
    if (this.dialog) delete this.dialog.dataset.b2hMeasurementConcept;
    this.triggeringElement?.focus();
    this.triggeringElement = null;
  }

  onDialogBackdropClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  onDialogKeydown(event) {
    if (event.key !== 'Tab') return;

    const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href]')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  initializeReveals() {
    const revealItems = this.querySelectorAll('.b2h-ns-reveal');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (
      reducedMotion ||
      window.Shopify?.designMode ||
      !('IntersectionObserver' in window)
    ) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -7% 0px', threshold: 0.08 },
    );

    revealItems.forEach((item) => this.revealObserver.observe(item));
  }
}

class B2HProgressMenu extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.details = this.querySelector('[data-b2h-progress-menu]');
    this.summary = this.details?.querySelector('summary');
    if (!this.details) return;

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.details.open) return;
      event.preventDefault();
      this.details.open = false;
      this.summary?.focus();
    }, { capture: true, signal: this.abortController.signal });

    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.details.open = false;
    }, { signal: this.abortController.signal });

    document.addEventListener('click', (event) => {
      if (this.details.open && !this.contains(event.target)) this.details.open = false;
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }
}

if (!customElements.get('b2h-progress-menu')) {
  customElements.define('b2h-progress-menu', B2HProgressMenu);
}

if (!customElements.get('b2h-north-star')) {
  customElements.define('b2h-north-star', B2HNorthStar);
}
